import { useState, useEffect, useCallback } from "react";
import { fetchVehicles, createVehicle, updateVehicle, deleteVehicle, fetchOrganizations } from "@/lib/api";
import { VEHICLE_TYPES, VEHICLE_TYPE_LABELS, VEHICLE_STATUSES, VEHICLE_STATUS_LABELS, FUEL_TYPES } from "@/lib/constants";
import { CAR_MODELS } from "@/lib/car-models";
import { getErrorMessage } from "@/lib/utils";
import { PlusCircle, Pencil, Trash, Car, Copy } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

function InputField({ label, testId, ...props }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">{label}</label>
      <input data-testid={testId} className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 bg-white font-medium" {...props} />
    </div>
  );
}

export default function FleetPage() {
  const [vehicles, setVehicles] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [form, setForm] = useState({ registration: "", make: "", model: "", year: 2024, type: "CAR", color: "", odometer: 0, fuel_type: "PETROL", branch: "", daily_rate: 0, notes: "" });
  const [customMake, setCustomMake] = useState(false);
  const [customModel, setCustomModel] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetchVehicles(filter !== "ALL" ? { status: filter } : {}),
      fetchOrganizations().catch(() => []),
    ])
      .then(([vehicleList, orgList]) => {
        setVehicles(vehicleList);
        setOrgs(orgList);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const activeOrg = orgs.find((org) => org.is_active);
  const canManageFleet = ["OWNER", "MANAGER"].includes(activeOrg?.role);

  const openNew = () => {
    setEditVehicle(null);
    setForm({ registration: "", make: "", model: "", year: 2024, type: "CAR", color: "", odometer: 0, fuel_type: "PETROL", branch: "", daily_rate: 0, notes: "" });
    setCustomMake(false);
    setCustomModel(false);
    setShowForm(true);
  };
  const openEdit = (v) => {
    setEditVehicle(v);
    setForm({ registration: v.registration, make: v.make, model: v.model, year: v.year, type: v.type, color: v.color, odometer: v.odometer, fuel_type: v.fuel_type, branch: v.branch, daily_rate: v.daily_rate, notes: v.notes, status: v.status });
    setCustomMake(!Object.keys(CAR_MODELS).includes(v.make || ""));
    setCustomModel(!(CAR_MODELS[v.make] || []).includes(v.model || ""));
    setShowForm(true);
  };
  const openDuplicate = (v) => {
    setEditVehicle(null);
    setForm({ registration: "", make: v.make, model: v.model, year: v.year, type: v.type, color: v.color, odometer: v.odometer, fuel_type: v.fuel_type, branch: v.branch, daily_rate: v.daily_rate, notes: v.notes });
    setCustomMake(!Object.keys(CAR_MODELS).includes(v.make || ""));
    setCustomModel(!(CAR_MODELS[v.make] || []).includes(v.model || ""));
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editVehicle) {
        await updateVehicle(editVehicle.id, form);
        toast.success("Vehicle updated");
      } else {
        await createVehicle(form);
        toast.success("Vehicle added");
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete ${v.make} ${v.model} (${v.registration})?`)) return;
    try { await deleteVehicle(v.id); toast.success("Vehicle deleted"); load(); }
    catch (err) { toast.error(getErrorMessage(err, "Cannot delete")); }
  };

  return (
    <div className="h-full overflow-y-auto" data-testid="fleet-page">
      <div className="max-w-6xl mx-auto p-6 lg:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">Fleet</h1>
            <p className="text-sm text-slate-500 mt-1 font-medium">{vehicles.length} vehicles</p>
          </div>
          {canManageFleet && (
            <button data-testid="fleet-add-btn" onClick={openNew}
              className="flex items-center gap-2 h-11 px-5 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 transition-colors">
              <PlusCircle weight="bold" className="w-5 h-5" /> Add Vehicle
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setFilter("ALL")} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${filter === "ALL" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>All</button>
          {VEHICLE_STATUSES.map(s => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${filter === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{VEHICLE_STATUS_LABELS[s]}</button>
          ))}
        </div>

        {/* Grid */}
        {loading ? <div className="text-center py-12 text-slate-400 text-sm">Loading...</div> : vehicles.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm">No vehicles found</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehicles.map(v => (
              <div key={v.id} data-testid={`fleet-vehicle-${v.registration}`}
                className="bg-white border border-slate-200 rounded-md p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 group">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded vtype-${v.type.toLowerCase()}`}>{VEHICLE_TYPE_LABELS[v.type]}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${v.status === "AVAILABLE" ? "bg-emerald-100 text-emerald-700" : v.status === "ON_HIRE" ? "bg-foreground/5 text-foreground/70" : v.status === "MAINTENANCE" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{VEHICLE_STATUS_LABELS[v.status]}</span>
                  </div>
                  {canManageFleet && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button data-testid={`fleet-duplicate-${v.registration}`} onClick={() => openDuplicate(v)} className="p-1.5 hover:bg-blue-50 rounded" title="Duplicate Vehicle"><Copy weight="bold" className="w-3.5 h-3.5 text-blue-500" /></button>
                      <button data-testid={`fleet-edit-${v.registration}`} onClick={() => openEdit(v)} className="p-1.5 hover:bg-slate-100 rounded" title="Edit Vehicle"><Pencil weight="bold" className="w-3.5 h-3.5 text-slate-500" /></button>
                      <button data-testid={`fleet-delete-${v.registration}`} onClick={() => handleDelete(v)} className="p-1.5 hover:bg-red-50 rounded" title="Delete Vehicle"><Trash weight="bold" className="w-3.5 h-3.5 text-red-500" /></button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-slate-100 rounded-md flex items-center justify-center">
                    <Car weight="duotone" className="w-6 h-6 text-slate-400" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-sm">{v.make} {v.model}</div>
                    <div className="text-xs text-slate-500">{v.year} &middot; {v.color}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <span className="font-mono text-xs font-bold text-slate-600">{v.registration}</span>
                  <span className="text-sm font-black text-foreground">${v.daily_rate}/day</span>
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                  <span>{v.odometer?.toLocaleString()} km</span>
                  <span>{v.fuel_type}</span>
                  <span>{v.branch}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editVehicle ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Registration *" testId="vehicle-registration" required value={form.registration} onChange={e => setForm(p => ({...p, registration: e.target.value.toUpperCase()}))} />
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Type *</label>
                <select data-testid="vehicle-type" className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md bg-white" value={form.type} onChange={e => setForm(p => ({...p, type: e.target.value}))}>
                  {VEHICLE_TYPES.map(t => <option key={t} value={t}>{VEHICLE_TYPE_LABELS[t]}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block flex justify-between items-center">
                  <span>Make *</span>
                  {customMake && <button type="button" onClick={() => { setCustomMake(false); setForm(p => ({...p, make: "", model: ""})); }} className="text-[10px] text-blue-500 hover:underline">Select from list</button>}
                </label>
                {customMake ? (
                  <input data-testid="vehicle-make-custom" required className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 bg-white font-medium" value={form.make} onChange={e => setForm(p => ({...p, make: e.target.value}))} />
                ) : (
                  <select data-testid="vehicle-make" required className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md bg-white font-medium" value={form.make} onChange={e => {
                    if (e.target.value === "OTHER") {
                      setCustomMake(true);
                      setCustomModel(true);
                      setForm(p => ({...p, make: "", model: ""}));
                    } else {
                      setForm(p => ({...p, make: e.target.value, model: ""}));
                      setCustomModel(false);
                    }
                  }}>
                    <option value="" disabled>Select Make</option>
                    {Object.keys(CAR_MODELS).sort().map(m => <option key={m} value={m}>{m}</option>)}
                    <option value="OTHER" className="font-bold">Other (Type manually)</option>
                  </select>
                )}
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block flex justify-between items-center">
                  <span>Model *</span>
                  {customModel && !customMake && <button type="button" onClick={() => { setCustomModel(false); setForm(p => ({...p, model: ""})); }} className="text-[10px] text-blue-500 hover:underline">Select from list</button>}
                </label>
                {customModel || customMake || !form.make ? (
                  <input data-testid="vehicle-model-custom" required className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 bg-white font-medium disabled:opacity-50" value={form.model} onChange={e => setForm(p => ({...p, model: e.target.value}))} disabled={!form.make && !customMake} placeholder={!form.make && !customMake ? "Select Make first" : ""} />
                ) : (
                  <select data-testid="vehicle-model" required className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md bg-white font-medium" value={form.model} onChange={e => {
                    if (e.target.value === "OTHER") {
                      setCustomModel(true);
                      setForm(p => ({...p, model: ""}));
                    } else {
                      setForm(p => ({...p, model: e.target.value}));
                    }
                  }}>
                    <option value="" disabled>Select Model</option>
                    {(CAR_MODELS[form.make] || []).sort().map(m => <option key={m} value={m}>{m}</option>)}
                    <option value="OTHER" className="font-bold">Other (Type manually)</option>
                  </select>
                )}
              </div>
              <InputField label="Year *" testId="vehicle-year" type="number" required min={1990} max={2030} value={form.year} onChange={e => setForm(p => ({...p, year: parseInt(e.target.value)}))} />
              <InputField label="Color" testId="vehicle-color" value={form.color} onChange={e => setForm(p => ({...p, color: e.target.value}))} />
              <InputField label="Odometer (km)" testId="vehicle-odometer" type="number" value={form.odometer} onChange={e => setForm(p => ({...p, odometer: parseInt(e.target.value) || 0}))} />
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Fuel Type</label>
                <select data-testid="vehicle-fuel" className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md bg-white" value={form.fuel_type} onChange={e => setForm(p => ({...p, fuel_type: e.target.value}))}>
                  {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <InputField label="Branch" testId="vehicle-branch" value={form.branch} onChange={e => setForm(p => ({...p, branch: e.target.value}))} />
              <InputField label="Daily Rate ($)" testId="vehicle-daily-rate" type="number" step="0.01" value={form.daily_rate} onChange={e => setForm(p => ({...p, daily_rate: parseFloat(e.target.value) || 0}))} />
            </div>
            {editVehicle && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Status</label>
                <select data-testid="vehicle-status" className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md bg-white" value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))}>
                  {VEHICLE_STATUSES.map(s => <option key={s} value={s}>{VEHICLE_STATUS_LABELS[s]}</option>)}
                </select>
              </div>
            )}
            <button data-testid="vehicle-save" type="submit" disabled={saving} className="w-full h-12 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors">
              {saving ? "Saving..." : editVehicle ? "Update Vehicle" : "Add Vehicle"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
